import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, Upload, Filter, ChevronDown } from 'lucide-react';
import MathRenderer from '../components/MathRenderer';

const QUESTION_FIELDS = {
  mcq: ['question_text', 'question_latex', 'option_a', 'option_b', 'option_c', 'option_d', 'correct_option', 'solution', 'solution_latex', 'hint', 'common_mistake'],
  fill_blank: ['question_text', 'question_latex', 'correct_answer', 'solution', 'solution_latex', 'hint'],
  true_false: ['question_text', 'correct_answer', 'solution'],
  match: ['question_text', 'match_pairs', 'correct_answer', 'solution'],
  subjective: ['question_text', 'question_latex', 'correct_answer', 'solution', 'solution_latex', 'hint'],
  multi_select: ['question_text', 'option_a', 'option_b', 'option_c', 'option_d', 'correct_answer', 'solution'],
  ordering: ['question_text', 'question_latex', 'correct_answer', 'solution'],
  assertion_reason: ['question_text', 'option_a', 'option_b', 'option_c', 'option_d', 'correct_option', 'solution'],
  integer_type: ['question_text', 'question_latex', 'correct_answer', 'solution', 'solution_latex', 'hint'],
  passage_based: ['question_text', 'correct_answer', 'solution'],
  diagram_based: ['question_text', 'question_latex', 'correct_answer', 'solution', 'solution_latex'],
  matrix_match: ['question_text', 'correct_answer', 'solution', 'solution_latex'],
};

const FIELD_LABELS = {
  question_text: 'Question Text', question_latex: 'LaTeX Version', option_a: 'Option A', option_b: 'Option B',
  option_c: 'Option C', option_d: 'Option D', correct_option: 'Correct Option (A/B/C/D)', correct_answer: 'Correct Answer',
  solution: 'Solution (step-by-step)', solution_latex: 'Solution LaTeX', hint: 'Hint', common_mistake: 'Common Mistake',
  match_pairs: 'Match Pairs (JSON)',
};

export default function QuestionManager() {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({ class_level: '', chapter: '', difficulty: '', question_type: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    class_level: 6, chapter: '', difficulty: 'foundation', question_type: 'mcq', marks: 1, micro_concept: '',
    question_text: '', question_latex: '', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_option: '', correct_answer: '', match_pairs: '', solution: '', solution_latex: '', hint: '', common_mistake: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetch(`${API}/config/all`).then(r => r.json()).then(setConfig).catch(() => {}); }, [API]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 15 });
    if (filters.class_level) params.set('class_level', filters.class_level);
    if (filters.chapter) params.set('chapter', filters.chapter);
    if (filters.difficulty) params.set('difficulty', filters.difficulty);
    if (filters.question_type) params.set('question_type', filters.question_type);
    try {
      const res = await fetch(`${API}/admin/questions?${params}`, { headers: getHeaders() });
      const data = await res.json();
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [API, getHeaders, page, filters]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const chapters = config?.config_chapters?.filter(c => c.class_key === String(form.class_level || filters.class_level)) || [];
  const difficulties = config?.config_difficulty_levels || [];
  const questionTypes = config?.config_question_types || [];

  const handleSave = async () => {
    if (!form.question_text) { toast.error('Question text required'); return; }
    try {
      const payload = { ...form, class_level: parseInt(form.class_level), marks: parseInt(form.marks) || 1 };
      if (editingId) {
        await fetch(`${API}/admin/questions/${editingId}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(payload) });
        toast.success('Updated!');
      } else {
        await fetch(`${API}/admin/questions`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(payload) });
        toast.success('Added!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchQuestions();
    } catch { toast.error('Save failed'); }
  };

  const handleEdit = (q) => {
    setForm({
      class_level: q.class_level || 6, chapter: q.chapter || '', difficulty: q.difficulty || 'foundation',
      question_type: q.question_type || 'mcq', marks: q.marks || 1, micro_concept: q.micro_concept || '',
      question_text: q.question_text || '', question_latex: q.question_latex || '',
      option_a: q.option_a || '', option_b: q.option_b || '', option_c: q.option_c || '', option_d: q.option_d || '',
      correct_option: q.correct_option || '', correct_answer: q.correct_answer || '',
      match_pairs: q.match_pairs || '', solution: q.solution || '', solution_latex: q.solution_latex || '',
      hint: q.hint || '', common_mistake: q.common_mistake || '',
    });
    setEditingId(q.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`${API}/admin/questions/${id}`, { method: 'DELETE', headers: getHeaders() });
    toast.success('Deleted');
    fetchQuestions();
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/admin/questions/import-csv`, {
        method: 'POST', headers: { 'Authorization': getHeaders()['Authorization'] }, body: formData,
      });
      const data = await res.json();
      toast.success(data.message);
      fetchQuestions();
    } catch { toast.error('Import failed'); }
  };

  const resetForm = () => setForm({
    class_level: 6, chapter: '', difficulty: 'foundation', question_type: 'mcq', marks: 1, micro_concept: '',
    question_text: '', question_latex: '', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_option: '', correct_answer: '', match_pairs: '', solution: '', solution_latex: '', hint: '', common_mistake: '',
  });

  const activeFields = QUESTION_FIELDS[form.question_type] || QUESTION_FIELDS.mcq;

  return (
    <div className="min-h-screen" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/admin')} data-testid="qm-back-btn"><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">Question Manager</h1>
        <span className="text-xs font-bold text-slate-400 ml-1">({total})</span>
        <div className="ml-auto flex gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={handleCSV} className="hidden" />
            <span className="flex items-center gap-1 text-xs font-bold text-[#1B4F8A] bg-[#F0E6D8] px-3 py-2 rounded-full"><Upload className="w-3 h-3" /> CSV</span>
          </label>
          <Button data-testid="add-question-btn" onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }} className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-bold rounded-full px-4 py-2 text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-4">
        {/* Filters */}
        <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 text-sm font-bold text-[#1B4F8A] mb-3">
          <Filter className="w-4 h-4" /> Filters <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <select data-testid="filter-class" value={filters.class_level} onChange={e => { setFilters({...filters, class_level: e.target.value}); setPage(1); }} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
              <option value="">All Classes</option>
              {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
            </select>
            <select data-testid="filter-type" value={filters.question_type} onChange={e => { setFilters({...filters, question_type: e.target.value}); setPage(1); }} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
              <option value="">All Types</option>
              {questionTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select data-testid="filter-difficulty" value={filters.difficulty} onChange={e => { setFilters({...filters, difficulty: e.target.value}); setPage(1); }} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
              <option value="">All Difficulty</option>
              {difficulties.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <Button onClick={() => { setFilters({ class_level: '', chapter: '', difficulty: '', question_type: '' }); setPage(1); }} variant="ghost" className="text-xs font-bold">Clear</Button>
          </div>
        )}

        {/* Question Form */}
        {showForm && (
          <Card className="mb-4 border-2 border-[#FF6B35] rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-[#FF6B35]">{editingId ? 'Edit' : 'New'} Question</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <select data-testid="form-class" value={form.class_level} onChange={e => setForm({...form, class_level: e.target.value, chapter: ''})} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
                  {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
                </select>
                <select data-testid="form-chapter" value={form.chapter} onChange={e => setForm({...form, chapter: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
                  <option value="">Chapter</option>
                  {chapters.map(ch => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
                </select>
                <select data-testid="form-difficulty" value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
                  {difficulties.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
                <select data-testid="form-type" value={form.question_type} onChange={e => setForm({...form, question_type: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold bg-white">
                  {questionTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Marks" type="number" value={form.marks} onChange={e => setForm({...form, marks: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] text-sm" />
                <Input placeholder="Micro Concept" value={form.micro_concept} onChange={e => setForm({...form, micro_concept: e.target.value})} className="rounded-xl border-2 border-[#E5DACE] text-sm" />
              </div>

              {/* Dynamic fields based on question type */}
              {activeFields.map(field => (
                <div key={field}>
                  <label className="text-xs font-bold text-slate-500 block mb-1">{FIELD_LABELS[field] || field}</label>
                  {field === 'solution' || field === 'solution_latex' || field === 'match_pairs' || field === 'question_text' ? (
                    <textarea
                      data-testid={`form-${field}`}
                      rows={field === 'question_text' || field === 'solution' ? 3 : 2}
                      value={form[field] || ''}
                      onChange={e => setForm({...form, [field]: e.target.value})}
                      className="w-full rounded-xl border-2 border-[#E5DACE] px-3 py-2 text-sm font-bold focus:border-[#1B4F8A] outline-none resize-y"
                      placeholder={field === 'match_pairs' ? '[{"left":"A","right":"1"},...]' : FIELD_LABELS[field]}
                    />
                  ) : (
                    <Input
                      data-testid={`form-${field}`}
                      value={form[field] || ''}
                      onChange={e => setForm({...form, [field]: e.target.value})}
                      className="rounded-xl border-2 border-[#E5DACE] text-sm"
                      placeholder={FIELD_LABELS[field]}
                    />
                  )}
                </div>
              ))}

              {/* Preview */}
              {form.question_text && (
                <div className="bg-[#F0E6D8] rounded-xl p-3">
                  <p className="text-xs font-bold text-slate-500 mb-1">Preview:</p>
                  <MathRenderer text={form.question_latex || form.question_text} />
                </div>
              )}

              <div className="flex gap-2">
                <Button data-testid="save-question-btn" onClick={handleSave} className="bg-[#06D6A0] hover:bg-[#04A77C] text-white font-bold rounded-full text-sm px-4"><Save className="w-3 h-3 mr-1" /> Save</Button>
                <Button onClick={() => { setShowForm(false); setEditingId(null); }} variant="ghost" className="font-bold rounded-full text-sm"><X className="w-3 h-3 mr-1" /> Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions List */}
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-20 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <div className="space-y-2">
            {questions.map(q => (
              <Card key={q.id} className="border-2 border-[#E5DACE] rounded-2xl">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#1B4F8A]/10 text-[#1B4F8A]">C{q.class_level}</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35]">{q.question_type || 'mcq'}</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#06D6A0]/10 text-[#06D6A0]">{q.difficulty}</span>
                        <span className="text-xs text-slate-400">{q.marks}m</span>
                      </div>
                      <div className="text-sm font-bold text-[#1B4F8A] line-clamp-2">
                        <MathRenderer text={q.question_latex || q.question_text} />
                      </div>
                      {q.chapter && <p className="text-xs text-slate-400 mt-1">{q.chapter}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => handleEdit(q)} className="p-1.5 hover:bg-[#F0E6D8] rounded-lg" data-testid={`edit-q-${q.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                      <button onClick={() => handleDelete(q.id)} className="p-1.5 hover:bg-red-50 rounded-lg" data-testid={`del-q-${q.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 15 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button disabled={page <= 1} onClick={() => setPage(p => p-1)} variant="ghost" className="text-sm font-bold">Prev</Button>
            <span className="text-sm font-bold text-slate-500 py-2">Page {page} of {Math.ceil(total/15)}</span>
            <Button disabled={page * 15 >= total} onClick={() => setPage(p => p+1)} variant="ghost" className="text-sm font-bold">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
